import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Bot, Search, FolderOpen, ChevronRight, ArrowRight, CheckCircle, Zap } from 'lucide-react';

const coreFeatures = [
  {
    id: 'assistant',
    icon: Bot,
    title: 'AI Assistant',
    description: 'Your intelligent copilot that reveals insights hidden inside contracts, reports, and project documents, helping you make faster, smarter decisions.',
    image: '/images/Ufficient Assistant.png', // Using the assistant image
    color: 'from-blue-500 to-indigo-600',
    bgColor: 'bg-blue-50',
    borderColor: 'border-blue-200',
    textColor: 'text-blue-600',
  },
  {
    id: 'search',
    icon: Search,
    title: 'Smart Search',
    description: 'Go beyond "Control + F." Ufficient\'s AI-powered search understands context and intent, so you always find the exact information you need, when you need it.',
    image: '/images/Ufficient Search.png', // Using search interface image
    color: 'from-emerald-500 to-teal-600',
    bgColor: 'bg-emerald-50',
    borderColor: 'border-emerald-200',
    textColor: 'text-emerald-600',
  },
  {
    id: 'files',
    icon: FolderOpen,
    title: 'Files',
    description: 'Centralize every document into a single secure hub. With built-in version control, audit trails, and tagging, silos disappear and collaboration flows seamlessly.',
    image: '/images/Files Module.png', // Using files/mobile interface image
    color: 'from-purple-500 to-violet-600',
    bgColor: 'bg-purple-50',
    borderColor: 'border-purple-200',
    textColor: 'text-purple-600',
  }
];

const coreModuleFeatures = [
  { 
    text: 'AI Assistant for Instant Answers – Surface insights from contracts, reports, and project documents in seconds.',
    icon: Bot 
  },
  { 
    text: 'Smart Document Search – AI-powered, context-aware search across engineering, legal, and construction files.',
    icon: Search 
  },
  { 
    text: 'Intelligent Tagging & Auto-Categorization – Organize proposals, contracts, and compliance docs automatically.',
    icon: Zap 
  },
  { 
    text: 'Version Control & Audit-Ready Tracking – Always access the latest file with compliance history intact.',
    icon: CheckCircle 
  }
];

const CoreHubShowcase = () => {
  const [activeFeature, setActiveFeature] = useState(0);

  return (
    <section className="py-24 bg-gradient-to-br from-slate-50 via-white to-blue-50 overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="mb-4"
          >
            <span className="text-blue-600 font-semibold text-sm sm:text-base uppercase tracking-wide">
              How Ufficient Works
            </span>
          </motion.div>

          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="text-4xl sm:text-5xl md:text-6xl font-bold text-gray-900 mb-8 leading-tight"
          >
            Your Core Hub for <span className="text-blue-600">AI-Driven Efficiency</span>
          </motion.h2>
        </div>

        {/* Feature Tabs */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="flex flex-col lg:flex-row justify-center mb-12"
        >
          <div className="flex flex-col sm:flex-row bg-white rounded-2xl p-2 shadow-lg border border-gray-100 max-w-2xl mx-auto lg:mx-0">
            {coreFeatures.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <button
                  key={feature.id}
                  onClick={() => setActiveFeature(index)}
                  className={`flex items-center justify-center px-6 py-4 rounded-xl font-medium transition-all duration-300 ${
                    activeFeature === index
                      ? `bg-gradient-to-r ${feature.color} text-white shadow-lg transform scale-105`
                      : 'text-gray-600 hover:bg-gray-50 hover:text-gray-800'
                  }`}
                >
                  <Icon size={20} className="mr-2" />
                  {feature.title}
                </button>
              );
            })}
          </div>
        </motion.div>

        {/* Feature Content */}
        <div className="grid lg:grid-cols-2 gap-12 items-center mb-24">
          {/* Feature Description */}
          <motion.div
            key={activeFeature}
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            className="lg:order-1"
          >
            <div className={`p-8 rounded-2xl ${coreFeatures[activeFeature].bgColor} border ${coreFeatures[activeFeature].borderColor}`}>
              <div className={`w-16 h-16 rounded-2xl bg-gradient-to-r ${coreFeatures[activeFeature].color} flex items-center justify-center mb-6`}>
                {React.createElement(coreFeatures[activeFeature].icon, { 
                  size: 32, 
                  className: "text-white" 
                })}
              </div>
              
              <h3 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">
                {coreFeatures[activeFeature].title}
              </h3>
              
              <p className="text-lg text-gray-700 leading-relaxed mb-8">
                {coreFeatures[activeFeature].description}
              </p>
              
              <Link to="/contact">
                <button className={`inline-flex items-center px-8 py-4 bg-gradient-to-r ${coreFeatures[activeFeature].color} text-white rounded-xl font-semibold hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1 group`}>
                  See Core Hub in Action
                  <ArrowRight size={20} className="ml-2 group-hover:translate-x-1 transition-transform" />
                </button>
              </Link>
            </div>
          </motion.div>

          {/* Feature Image */}
          <motion.div
            key={`image-${activeFeature}`}
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            className="lg:order-2"
          >
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-r from-blue-400 to-purple-500 rounded-2xl opacity-10 blur-xl transform rotate-2 scale-105"></div>
              <div className="relative bg-white rounded-2xl shadow-2xl overflow-hidden border border-gray-100">
                <img
                  src={coreFeatures[activeFeature].image}
                  alt={`${coreFeatures[activeFeature].title} Interface`}
                  className="w-full h-auto object-cover"
                  loading="lazy"
                />
              </div>
              
              {/* Feature indicator dots */}
              <div className="absolute -bottom-4 left-1/2 transform -translate-x-1/2 flex space-x-2">
                {coreFeatures.map((_, index) => (
                  <div
                    key={index}
                    className={`w-3 h-3 rounded-full transition-colors duration-300 ${
                      index === activeFeature ? 'bg-blue-600' : 'bg-gray-300'
                    }`}
                  />
                ))}
              </div>
            </div>
          </motion.div>
        </div>

        {/* Bottom CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="text-center"
        >
          <p className="text-lg text-gray-600 mb-8">
            Ready to transform how your team works with documents and data?
          </p>
          <Link to="/contact">
            <button className="inline-flex items-center px-10 py-4 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl text-lg font-semibold hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 group">
              Get Started Today
              <ChevronRight size={24} className="ml-2 group-hover:translate-x-1 transition-transform" />
            </button>
          </Link>
        </motion.div>
      </div>
    </section>
  );
};

export default CoreHubShowcase; 